<template>
    <b-modal v-model="show" title="Ajouter une campagne Google Ads" :hide-footer="true">
        <component
            :is="currentStepComponent"
            :campaign="campaign"
            :adGroups="adGroups"
            :currentAdGroupIndex="currentAdGroupIndex"
            :editingAdIndex="editingAdIndex"
            :spinner="addSpinnerToCreateBtn"
            @next="nextStep"
            @back="previousStep"
            @addAd="addAd"
            @editAd="editAd"
            @cancelEdit="cancelEdit"
            @saveAd="saveAd"
            @addAdGroup="addAdGroup"
            @deleteAdGroup="deleteAdGroup"
            @create="createCampaign"
        />
    </b-modal>
</template>

<script>
    import CampaignGoogleStep1 from './GoogleCampaignsSteps/campaign-google-step1.vue';
    import CampaignGoogleStep2 from './GoogleCampaignsSteps/campaign-google-step2.vue';
    import CampaignGoogleStep3 from './GoogleCampaignsSteps/campaign-google-step3.vue';

    export default {
        components: {
            CampaignGoogleStep1,
            CampaignGoogleStep2,
            CampaignGoogleStep3,
        },
        data() {
            return {
                show: false,
                step: 1,
                campaign: {
                    childCampaignCode: '',
                    name: '',
                    budget: 0,
                    cpcMax: 1,
                    logo: null,
                    startDate: '',
                    endDate: '',
                },
                adGroups: [{ name: '', keywords: '', ads: [] }],
                currentAdGroupIndex: 0,
                editingAdIndex: null,
                childCampaign: {},
                addSpinnerToCreateBtn: false,
            };
        },
        computed: {
            currentStepComponent() {
                return [CampaignGoogleStep1, CampaignGoogleStep2, CampaignGoogleStep3][this.step - 1];
            },
        },
        methods: {
            async open(campaignCode, childCampaignCode) {
                try {
                    this.show = true;
                    const childCampaign = await this.$store.dispatch('app/getCampaign', { campaignCode: childCampaignCode });
                    const parentCampaign = await this.$store.dispatch('app/getCampaign', { campaignCode: campaignCode });
                    this.$set(this, 'childCampaign', childCampaign.data);

                    const { media_support_code, point_code, start_date, brand } = childCampaign.data;
                    const startMonth = new Date(start_date).toLocaleString('default', { month: 'long' });

                    this.campaign.childCampaignCode = childCampaignCode;
                    this.campaign.startDate = this.childCampaign.start_date;
                    this.campaign.endDate = this.childCampaign.end_date;

                    const crea = parentCampaign.data.details?.crea;

                    const customerCity = parentCampaign.data.details?.customer?.city ?? '';
                    const creaName = crea.name ?? '';
                    this.campaign.name = `${brand}_${point_code}_${customerCity}_${creaName}_${media_support_code}_${startMonth}${new Date(start_date).getFullYear()}`.toUpperCase();

                    this.adGroups[0].name = this.campaign.name + '_' + this.adGroups.length;

                    await this.preFillAdGroupForm(brand, crea.code, media_support_code);

                    const campaignDurationDays = (new Date(this.childCampaign.end_date) - new Date(this.childCampaign.start_date)) / (1000 * 60 * 60 * 24);
                    const campaignAmount = this.childCampaign.details.amount_without_margin ?? this.childCampaign.details.amount;
                    this.campaign.budget = Math.round(campaignAmount / (campaignDurationDays + 1) - 0.04) / 100;
                } catch (error) {
                    console.error('Error getting campaign infos', error);
                }
            },
            nextStep() {
                this.step++;
            },
            previousStep() {
                this.step--;
            },
            addAd(groupIndex) {
                this.currentAdGroupIndex = groupIndex;
                this.editingAdIndex = null;
                this.step = 3;
            },
            editAd(groupIndex, adIndex) {
                this.currentAdGroupIndex = groupIndex;
                this.editingAdIndex = adIndex;
                this.step = 3;
            },
            cancelEdit() {
                this.step = 2;
            },
            saveAd(adData) {
                const ads = this.adGroups[this.currentAdGroupIndex].ads;
                if (this.editingAdIndex === null) {
                    ads.push(adData);
                } else {
                    this.$set(ads, this.editingAdIndex, adData);
                }
                this.step = 2;
            },
            addAdGroup() {
                this.adGroups.push({ name: this.campaign.name + '_' + (this.adGroups.length + 1), keywords: '', ads: [] });
            },
            deleteAdGroup(groupIndex) {
                this.adGroups.splice(groupIndex, 1);
            },
            async createCampaign() {
                try {
                    this.addSpinnerToCreateBtn = true;
                    let response = '';
                    if (!this.campaign.logo) {
                        const campaignCopy = { ...this.campaign };
                        delete campaignCopy.logo;
                        response = (await this.$store.dispatch('app/createGoogleCampaign', { campaign: campaignCopy, adGroups: this.adGroups })).data;
                    } else {
                        const { base64, type } = await this.convertToBase64(this.campaign.logo);
                        const campaignCopy = { ...this.campaign };
                        delete campaignCopy.logo;
                        const campaignData = {
                            campaign: campaignCopy,
                            adGroups: this.adGroups,
                            logo: {
                                base64,
                                type,
                            },
                        };
                        response = (await this.$store.dispatch('app/createGoogleCampaign', campaignData)).data;
                    }
                    this.resetData();
                    this.$toast({
                        component: 'toast',
                        props: {
                            title: response,
                            icon: 'CheckCircleIcon',
                            variant: 'success',
                        },
                    });
                } catch (e) {
                    this.addSpinnerToCreateBtn = false;
                    console.error(e);
                    let errorMessage = 'Erreur lors de la création de la campagne';
                    if (e.response && e.response.data && e.response.data.error) {
                        errorMessage = e.response.data.error;
                    } else if (e.message) {
                        errorMessage = e.message;
                    }
                    this.$toast({
                        component: 'toast',
                        props: {
                            title: errorMessage,
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                        },
                    });
                }
            },
            convertToBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => {
                        const result = reader.result;
                        const base64 = result.split(',')[1];
                        resolve({ base64, type: file.type.split('/')[1] });
                    };
                    reader.onerror = (error) => reject(error);
                });
            },
            resetData() {
                this.addSpinnerToCreateBtn = false;
                this.show = false;
                this.step = 1;

                this.campaign = {
                    name: '',
                    budget: 0,
                    cpcMax: 1,
                    logo: null,
                    startDate: '',
                    endDate: '',
                };
                this.adGroups = [{ name: '', keywords: '', ads: [] }];
            },
            async preFillAdGroupForm(brand, creaCode, mediaCode) {
                try {
                    const themeConfig = await this.$store.dispatch('app/getFormattedThemeConfig', { brand, creaCode, mediaCode, posCode: this.childCampaign.point_code });
                    const posInfos = await this.$store.dispatch('app/getPosInfos', { brand, pointCode: this.childCampaign.point_code });
                    const creaInfos = await this.$store.dispatch('app/getCreaInfos', { brand, creaCode });

                    if (themeConfig) {
                        const fields = themeConfig.details;
                        const titles = [fields.title1, fields.title2, fields.title3];
                        const descriptions = [fields.description1, fields.description2];
                        const url = posInfos.details?.url && creaInfos.details?.landing_page_url ? `${posInfos.details?.url}${creaInfos.details?.landing_page_url}` : '';

                        this.adGroups[0] = {
                            ...this.adGroups[0],
                            keywords: fields.adwKeyWords.join(','),
                            ads: [
                                {
                                    finalUrl: url,
                                    path1: '',
                                    path2: '',
                                    titles: titles,
                                    descriptions: descriptions,
                                },
                            ],
                        };
                    }
                } catch (error) {
                    console.error(error);
                }
            },
        },
    };
</script>

<style scoped></style>
